import { ChangeDetectionStrategy, Component, inject, PLATFORM_ID } from '@angular/core';
import { RelatedProductsService } from '@df/catalog-ui/related-products/related-products.service';
import { app } from '@df/core/app';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { RelatedProductsComponent } from '../../catalog/product/related-products/related-products.component';
import { ProductViewLayoutComponent } from '../../catalog/product/view/layout/product-view-layout.component';
import { NTextBox, TextBoxComponent } from '../../common/text-box/text-box.component';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace NCmsProductCarousel {
    export interface IComponentData extends NICmsContentComponent.IData, NTextBox.IData {
        sections: ISection[];
    }

    export interface ISection {
        /**
         * Section title
         */
        title: string;
        /**
         * Max items qty
         */
        limit: number;
        /**
         * Source context
         */
        context?: string;
        /**
         * Engine
         */
        engine: string;
        /**
         * Products ids (manual engine)
         */
        productsIds: number[];
        /**
         * Categories ids (category engine)
         */
        categoriesIds: number[];
    }

    export enum EEngine {
        category = 'category',
        manual = 'manual',
        recentlyViewed = 'recent'
    }

    export enum ESoruce {
        cms = 'cms',
        product = 'product',
        basket = 'basket'
    }
}

let uid = 0;
@Component({
    imports: [RelatedProductsComponent, TextBoxComponent, ICmsClientModule],
    selector: 'cms-product-carousel',
    templateUrl: './cms-product-carousel.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<CmsProductCarouselComponent>({
    id: 'product-carousel',
    name: 'Product carousel',
    tags: ['carousel', 'product carousel'],
    fields: [
        ...NTextBox.getFields(false, false),
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'sections',
            title: 'Sections',
            itemName: 'Section',
            itemNameField: 'title',
            defaultSize: 1,
            complex: true,
            fields: [
                {
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Section title',
                    default: 'You may also like',
                    required: true
                },
                {
                    key: 'limit',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Max products qty',
                    required: true,
                    default: 12
                },
                <NICmsContentField.IChoiceField>{
                    key: 'context',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Context',
                    options: () =>
                        app?.meta?.model?.type === 'product'
                            ? [
                                  { key: NCmsProductCarousel.ESoruce.cms, title: NCmsProductCarousel.ESoruce.cms },
                                  { key: NCmsProductCarousel.ESoruce.product, title: NCmsProductCarousel.ESoruce.product },
                                  { key: NCmsProductCarousel.ESoruce.basket, title: NCmsProductCarousel.ESoruce.basket }
                              ]
                            : [
                                  { key: NCmsProductCarousel.ESoruce.cms, title: NCmsProductCarousel.ESoruce.cms },
                                  { key: NCmsProductCarousel.ESoruce.basket, title: NCmsProductCarousel.ESoruce.basket }
                              ],
                    default: NCmsProductCarousel.ESoruce.cms,
                    required: true
                },

                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'engine',
                    title: 'Engine',
                    options: (data: NCmsProductCarousel.ISection) =>
                        data.context !== NCmsProductCarousel.ESoruce.cms
                            ? Object.keys(RelatedProductsService.engines)
                                  .filter(i => !['manual', 'recent'].includes(i))
                                  .map(i => ({ key: i, title: i }))
                            : [
                                  NCmsProductCarousel.EEngine.category,
                                  NCmsProductCarousel.EEngine.manual,
                                  NCmsProductCarousel.EEngine.recentlyViewed
                              ].map(i => ({ key: i, title: i })),
                    required: true,
                    default: 'manual'
                },
                <NICmsContentField.IRelationField>{
                    key: 'categoriesIds',
                    type: NICmsContentField.EFieldType.relation,
                    title: 'Category',
                    options: {
                        title: 'Category',
                        type: 'category',
                        multiple: true
                    },
                    required: true,
                    canShow: (data: NCmsProductCarousel.ISection) =>
                        data?.engine === NCmsProductCarousel.EEngine.category && data?.context === NCmsProductCarousel.ESoruce.cms
                },
                <NICmsContentField.IRelationField>{
                    key: 'productsIds',
                    type: NICmsContentField.EFieldType.relation,
                    title: 'Items',
                    options: {
                        title: 'Product',
                        type: 'product',
                        multiple: true
                    },
                    required: true,
                    default: [-100, -101, -102, -103, -104],
                    canShow: (data: NCmsProductCarousel.ISection) => data?.engine === NCmsProductCarousel.EEngine.manual
                }
            ]
        }
    ]
})
export class CmsProductCarouselComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component?: NCmsProductCarousel.IComponentData;

    /**
     * Positions (indexes in component sections) of the sections that do have any products to show.
     * Tracked by position, not by object reference, as component content can be replaced with a deep-equal copy
     */
    private printedPositions: number[] = [];
    private selectedPosition: number | undefined;
    private hasPendingDetectChanges = false;

    readonly uid = `productCarousel${uid++}`;

    /**
     * List of visible section (ones that do have any products to show)
     */
    get visibleSections(): NCmsProductCarousel.ISection[] {
        const sections = this.component?.sections ?? [];
        return this.printedPositions.filter(position => position < sections.length).map(position => sections[position]);
    }

    private readonly productViewLayoutComponent = inject(ProductViewLayoutComponent, { optional: true });

    /**
     * Set once products have had long enough to arrive, see reservedHeight
     */
    private isReservationReleased = false;
    private reservationTimeout?: ReturnType<typeof setTimeout>;
    private readonly isBrowser = inject(PLATFORM_ID) === 'browser';

    override ngOnInit(): void {
        super.ngOnInit();
        if (this.isBrowser) {
            this.reservationTimeout = setTimeout(() => {
                this.isReservationReleased = true;
                this.scheduleDetectChanges();
            }, 10000);
        }
    }

    override ngOnDestroy(): void {
        clearTimeout(this.reservationTimeout);
        super.ngOnDestroy();
    }

    /**
     * Height held for the carousel until its products arrive.
     *
     * The products are fetched in the browser, so the server rendered only the padding and the
     * block grew by about 600px when they landed, moving everything below it. Measured on the
     * homepage carousel (title, section tabs, one row of slides) on phones and tablets, where
     * the slides scale with the viewport width, so it is only held for that shape, and only
     * for sections that should have products: recently viewed is often empty and would leave
     * a gap. If nothing arrives the hold is dropped after 10s.
     */
    protected get reservedHeight(): string | undefined {
        if (this.client.isCms || this.visibleSections.length || this.isReservationReleased) {
            return undefined;
        }
        if (!this.client.isS && !this.client.isM) {
            return undefined;
        }
        const sections = this.component?.sections || [];
        const expectsProducts = sections.every(
            section =>
                (!section.context || section.context === NCmsProductCarousel.ESoruce.cms) &&
                (section.engine === NCmsProductCarousel.EEngine.category ||
                    (section.engine === NCmsProductCarousel.EEngine.manual && !!section.productsIds?.length))
        );
        if (sections.length < 2 || !expectsProducts) {
            return undefined;
        }
        return this.client.isS ? 'calc(158px + 137.1vw)' : 'calc(274px + 58.6vw)';
    }

    /**
     * Retrieves related product component options
     */
    protected getOptions(section: NCmsProductCarousel.ISection) {
        return {
            engines:
                section.engine === NCmsProductCarousel.EEngine.recentlyViewed
                    ? NCmsProductCarousel.EEngine.recentlyViewed
                    : [
                          {
                              id: section.engine,
                              options: {
                                  // manual-engine
                                  ids: section.engine === NCmsProductCarousel.EEngine.manual ? section.productsIds : undefined,
                                  // category-engine
                                  categoryIds:
                                      section.engine === NCmsProductCarousel.EEngine.category &&
                                      section.context === NCmsProductCarousel.ESoruce.cms
                                          ? section.categoriesIds
                                          : undefined
                              }
                          }
                      ]
        };
    }

    /**
     * Retrieves related product component source
     */
    protected getSource(section: NCmsProductCarousel.ISection) {
        return section.context === NCmsProductCarousel.ESoruce.product && this.productViewLayoutComponent?.product
            ? this.productViewLayoutComponent.product
            : section.context === NCmsProductCarousel.ESoruce.basket
              ? 'basket'
              : undefined;
    }

    /**
     * If componentn uses multiple sections
     */
    get hasManySections(): boolean {
        return this.visibleSections.length > 1;
    }

    /**
     * Selected section, resolved against the current component sections
     */
    get selectedSection(): NCmsProductCarousel.ISection | undefined {
        return this.selectedPosition !== undefined && this.printedPositions.includes(this.selectedPosition)
            ? this.component?.sections?.[this.selectedPosition]
            : undefined;
    }

    protected onPrinted(hasModels: boolean, position: number) {
        const exists = this.printedPositions.includes(position);
        if (hasModels && !exists) {
            this.printedPositions = [...this.printedPositions, position].sort((a, b) => a - b);
        } else if (!hasModels && exists) {
            this.printedPositions = this.printedPositions.filter(item => item !== position);
        }

        this.selectedPosition = this.printedPositions[0];
        this.scheduleDetectChanges();
    }

    protected isVisible(section: NCmsProductCarousel.ISection): boolean {
        return this.selectedSection === section;
    }

    protected selectSection(section: NCmsProductCarousel.ISection): void {
        const position = this.component?.sections.indexOf(section) ?? -1;
        if (position > -1 && this.selectedPosition !== position) {
            this.selectedPosition = position;
            this.scheduleDetectChanges();
        }
    }

    private scheduleDetectChanges(): void {
        if (this.hasPendingDetectChanges) {
            return;
        }
        this.hasPendingDetectChanges = true;
        Promise.resolve().then(() => {
            this.hasPendingDetectChanges = false;
            this.detectChanges();
        });
    }
}
